import * as React from 'react'
import styled from 'styled-components'
import { LightenDarkenColor } from '~/utils/helpers'
import {
  ErrorOutlineIcon,
  InfoOutlineIcon,
  SuccessOutlineIcon,
  WarningOutlineIcon,
  CloseOutlineIcon,
} from '~/components/icons'

type IAlert = {
  type: 'success' | 'info' | 'danger' | 'warning' | 'extreme'
  message: React.ReactNode
  title?: string
  icon?: boolean // display the icon will be based on type
  onClose?: () => void
  showCloseButton?: boolean
}

const bgColor = {
  success: '#edf7ed',
  info: '#F8F8F8',
  warning: '#fff4e5',
  danger: '#fdecea',
  extreme: '#dc3545',
}

const AlertWrapper = styled.div<Partial<IAlert>>`
  background-color: ${(props) =>
    props.type ? bgColor[props.type] : bgColor['success']};
  border-width: 1px;
  border-style: solid;
  border-color: ${(props) =>
    props.type
      ? LightenDarkenColor(bgColor[props.type], -10)
      : LightenDarkenColor(bgColor['success'], -10)};
  color: ${(props) =>
    props.type === 'extreme'
      ? '#ffffff'
      : props.theme.body.textColor || '#212b36'};
  border-radius: ${(props) => props.theme.border.radius || '6px'};
  box-shadow:
    0px 2px 1px -1px rgba(0, 0, 0, 0.2),
    0px 1px 1px 0px rgba(0, 0, 0, 0.14),
    0px 1px 3px 0px rgba(0, 0, 0, 0.12);
  padding: 10px 12px;
  margin: 0 0 12px;
  font-size: 0.825rem;
  text-align: left;
  display: flex;
  position: relative;

  p {
    margin: 0 0 0;
  }

  strong {
    font-weight: 700;
  }

  svg {
    width: 22px;
    fill: ${(props) =>
      props.type ? props.theme[props.type] : props.theme['success']};
    display: inline-block;
    vertical-align: middle;
    margin-right: 2px;
    margin-top: -1px;
  }
`

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1rem;
  cursor: pointer;
  position: absolute;
  top: 8px;
  right: 8px;
  padding: 0;
  display: flex;
  align-items: center;
`

const icons: { [key: string]: React.ReactNode } = {
  success: <SuccessOutlineIcon />,
  info: <InfoOutlineIcon />,
  danger: <ErrorOutlineIcon />,
  warning: <WarningOutlineIcon />,
  close: <CloseOutlineIcon />,
}

export function Alert(props: IAlert) {
  const [isVisible, setIsVisible] = React.useState(true)

  const handleClose = () => {
    setIsVisible(false)
    if (props.onClose) {
      props.onClose()
    }
  }

  if (!isVisible) return null // Alert will not be rendered if it's not visible
  return (
    <AlertWrapper type={props.type || 'success'}>
      <div>{props.icon && icons[props.type]}</div>
      <div>
        {props.title && <strong>{props.title}</strong>} {props.message}
      </div>
      {props.showCloseButton && (
        <CloseButton onClick={handleClose}>{icons['close']}</CloseButton>
      )}
    </AlertWrapper>
  )
}
